/**
 * Soft≠Live Page168 + Page218 — Platform Control residual liquid (page132/84 + dual status).
 * Mount: /control/{tab}
 * Buses:
 *   GET /control/status (always-on Live posture · dual platform ∥ org)
 *   domains · custody · ng-shares · federation · org shares · security · backup
 * Soft residual deleted:
 *  - Soft invent federation status=live / Soft email-domain lists
 *  - Soft empty matrix when domains gated
 *  - Soft open of staff tabs for user seats
 *  - bare sandpit chrome without posture SoT
 *  - Soft invent platform tabs when platform plane sealed (Page218)
 *  - Soft invent org shares when org plane sealed (Page218)
 */
import { useState, useRef, useMemo } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, Ticker, go } from "./ui.js";
import { api, path, useAction, useApi, usePolled } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { SecurityDesk } from "./platform-security-desk.js";
import { Custody } from "./sa-custody.js";
import { ReproductionDesk } from "./reproduction-desk.js";
import { formatUserInstant } from "./time.js";

/** Soft≠Live Page218: missing contract op must not crash Control. */
function safeControlPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__control_status") return "/control/status";
    if (op === "get__sa_platform_domains") return "/sa/platform/domains";
    if (op === "get__shares") return "/shares";
    if (op === "get__federation_metrics") return "/federation/metrics";
    if (op === "get__federation_tenants") return "/federation/tenants";
    if (op === "get__admin_backup_schedule") return "/admin/backup/schedule";
    if (op === "get__admin_pg-backup_status" || op === "get__admin_pg_backup_status") {
      return "/admin/pg-backup/status";
    }
    return "";
  }
}

/** Path-free operator copy for Control desk. Soft invent planes forbidden. */
export function softControlErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient/.test(low)) {
    return "Your account cannot open this Control seat. Ask a platform admin if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That Control seat is not available.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|disabled/.test(low)) {
    return "Control is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/control\/|\/sa\/|\/shares|\/federation|GET |POST |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Pure: dual seat chips from Live Control posture (Page218). Fail-closed · === true only. */
export function controlSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const tabMax = Number(lim.tab_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    platformAllowed: p.platform_plane_allowed === true,
    orgAllowed: p.org_plane_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    writeAllowed: p.write_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    tabPaintMax:
      Number.isFinite(tabMax) && tabMax > 0 ? Math.floor(tabMax) : 12,
  };
}

/** Pure: share rows from GET /shares. */
export function shareRowsOf(pack) {
  const root = pack || {};
  const rows = root.items || root.shares || root.rows || [];
  return Array.isArray(rows) ? rows : [];
}

/**
 * Pure: control-plane rows from GET /sa/platform/domains.
 * Live fields are control_plane_domains — never invent email DNS domains.
 */
export function controlPlaneOf(pack) {
  const root = pack || {};
  const rows = root.control_plane_domains;
  return Array.isArray(rows) ? rows : [];
}

/** Pure: residual (still-legacy) domains. */
export function residualDomainsOf(pack) {
  const root = pack || {};
  const rows = root.sqlite_residual_domains;
  return Array.isArray(rows) ? rows : [];
}

/** @deprecated Soft path — items/domains empty against Domain matrix SoT. Prefer controlPlaneOf. */
export function domainRowsOf(pack) {
  return controlPlaneOf(pack);
}

/** Pure: federation tenant rows. */
export function fedTenantRowsOf(pack) {
  const root = pack || {};
  const rows = root.items || root.tenants || root.rows || [];
  return Array.isArray(rows) ? rows : [];
}

/**
 * Pure: metrics item — Live body may be flat or `{ item: {...} }`.
 * Never invent status=live when the field is absent.
 */
export function federationMetricsItemOf(pack) {
  const root = pack && typeof pack === "object" ? pack : {};
  if (root.item && typeof root.item === "object") return root.item;
  if (root.federation_id != null || root.tenant_count != null || root.device_total != null) {
    return root;
  }
  return {};
}

function flagCell(v) {
  if (v === true) return "on";
  if (v === false) return "off";
  return "—";
}

function storeLabel(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "—";
  if (s === "pg" || s === "postgres" || s === "postgresql") return "Primary database";
  if (s.includes("pg_write_sor") || s === "pg_sor") return "Primary database (write SoR)";
  if (s === "sqlite" || s.includes("sqlite")) return "Legacy local store";
  if (s.includes("redis")) return "Fast cache";
  return String(raw);
}

function domainLabel(id) {
  const s = String(id || "").trim();
  if (!s) return "—";
  const map = {
    device_ownership: "Device ownership",
    device_acl: "Device access",
    dashboard_users: "Console users",
    dashboard_users_local: "Local user mirror",
    admin_user_scopes: "Child admin access",
    tenant_delegations: "Delegated access",
    notification_group_shares: "Group shares",
    role_policies: "Role policies",
    billing: "Billing",
    audit: "Audit trail",
    alarm_feed: "Map alarm feed",
    export_jobs: "Export / report jobs",
    messages: "Device messages",
    ota_jobs: "Firmware update jobs",
    scheduled_commands: "Scheduled commands",
  };
  if (map[s]) return map[s];
  return s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function residualNote(notes) {
  let s = String(notes || "").trim();
  if (!s) return "—";
  return s
    .replace(/\bSQLite\b/gi, "legacy local store")
    .replace(/\bPostgres(?:ql)?\b|\bPG\b/g, "primary database")
    .replace(/\bMQTT\b/g, "device link")
    .replace(/\bRedis\b/gi, "fast cache")
    .replace(/\bOTA\b/g, "Firmware");
}

function formatUpdatedMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return formatUserInstant(n);
}

function numOrDash(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function _catalogOn(me, key) {
  const feats = (me && me.features) || {};
  const cat = (me && me.catalog) || {};
  if (feats[key] === true || cat[key] === true) return true;
  const caps = (me && me.caps) || [];
  return Array.isArray(caps) && caps.includes(key);
}

const TABS = [
  { id: "domains", label: "Domains", sa: true },
  { id: "custody", label: "Custody", sa: true },
  { id: "ng-shares", label: "Group shares", sa: true },
  { id: "federation", label: "Multi-site HQ", sa: true },
  { id: "shares", label: "Org shares", sa: false },
  { id: "security", label: "Security", sa: true },
  { id: "backup", label: "Backup", sa: true },
  /* Wave 16 — R0–R4 ladder; SA/admin read; mutate SA OTA/Security only. */
  { id: "reproduction", label: "Reproduction", sa: true },
];

/** Pure: domains KPI strip from Domain matrix only — Soft invent counts deleted. */
export function domainsKpisOf(pack) {
  const root = pack && typeof pack === "object" ? pack : {};
  const plane = controlPlaneOf(root);
  const residual = residualDomainsOf(root);
  const honesty = (root.honesty && typeof root.honesty === "object") ? root.honesty : {};
  return {
    plane: plane.length,
    residual: residual.length,
    primaryDb: root.platform_pg_enabled === true ? 1 : (root.platform_pg_enabled === false ? 0 : null),
    fullPg: honesty.claim_full_pg === true ? 1 : (honesty.claim_full_pg === false ? 0 : null),
  };
}

export default function PlatformControl({ sub, toast }) {
  const { me } = useSession();
  const posture = useApi(safeControlPath("get__control_status"));
  const post = posture.data || {};
  const role = String((me && me.role) || post.principal_role || "");
  const isRoot = role === "superadmin";
  const isStaff = role === "sa_staff";
  const isSa = isRoot || isStaff;
  // Fail-closed while loading/empty — Soft plane invent from me.role deleted.
  const seat = controlSeatOf(
    posture.loading || !posture.data
      ? { enabled: false, principal_role: role, limits: { tab_paint_max: 12 } }
      : post,
  );
  const canOpen = !posture.loading
    && seat.enabled === true
    && (seat.platformAllowed === true || seat.orgAllowed === true);
  const tabsLive = Array.isArray(post.tabs_allowed) ? post.tabs_allowed : null;
  const tabAllowed = (id) => {
    if (posture.loading || seat.enabled !== true) return false;
    /* Reproduction ladder: prefer Live tabs_allowed (backend ACL); role fallback if older bus. */
    if (id === "reproduction") {
      if (tabsLive) return tabsLive.includes("reproduction");
      return seat.platformAllowed === true || role === "admin" || isSa || isRoot;
    }
    if (tabsLive) return tabsLive.includes(id);
    if (id === "shares") return seat.orgAllowed === true;
    if (seat.platformAllowed !== true) return false;
    const meta = TABS.find((t) => t.id === id);
    return !(meta && meta.sa) || seat.platformAllowed === true;
  };
  const defaultTab = seat.platformAllowed === true
    ? (tabAllowed("domains") ? "domains" : (tabsLive && tabsLive[0]) || (seat.orgAllowed === true ? "shares" : "domains"))
    : "shares";
  const tab = TABS.find((t) => t.id === sub) && tabAllowed(sub) ? sub : defaultTab;
  const tabsPaint = TABS.filter((t) => tabAllowed(t.id)).slice(0, seat.tabPaintMax);
  // Live shares_allowed only — admin/role invent of shares pane deleted.
  const sharesPaneOpen = post.shares_allowed === true;
  const domainsPaneOpen = post.domains_allowed === true;
  const ngSharesPaneOpen = post.ng_shares_allowed === true;
  const federationPaneOpen = post.federation_allowed === true;
  const backupPaneOpen = post.backup_allowed === true;

  const reRead = () => {
    if (seat.reReadAllowed !== true) {
      toast && toast("Re-read is closed for this account", "warn");
      return;
    }
    posture.reload && posture.reload();
    toast && toast("Control status refreshed", "go");
  };

  if (!me) {
    return html`<div className="pc-control-desk pc-control-desk--liquid"
      data-cmc-page="page132-control" data-cmc-legacy="page84-control" data-cmc-control="page37"
      data-cmc-residual="page218-control">
      <${Alert} tone="warn" title="No session" say="Sign in to open platform control."/>
    </div>`;
  }
  if (posture.loading && !posture.data) {
    return html`<div className="pc-control-desk pc-control-desk--liquid"
      data-cmc-page="page132-control" data-cmc-legacy="page84-control" data-cmc-control="page37"
      data-cmc-residual="page218-control">
      <p className="note">Confirming Control seat…</p>
    </div>`;
  }
  if (!canOpen) {
    return html`<div className="pc-control-desk pc-control-desk--liquid"
      data-cmc-page="page132-control" data-cmc-legacy="page84-control" data-cmc-control="page37"
      data-cmc-residual="page218-control">
      <div className="pc-control-desk__rail">
        <div>
          <p className="eyebrow">Platform control · dual seat</p>
          <h1 className="pc-control-desk__title">Control · sealed</h1>
          <p className="note">Confirming which Control seats your account may open.</p>
        </div>
        <div className="btn-row pc-control-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${posture.loading || seat.reReadAllowed !== true}
            onClick=${reRead}><${Ico} d="refresh" w=${13}/> Re-read</button>
        </div>
      </div>
      <div className="pc-control-desk__kpis" aria-label="Control dual buses">
        <div className="pc-control-desk__kpi" data-on="0"><span>Platform seat</span><b>Sealed</b><em>SA tabs</em></div>
        <div className="pc-control-desk__kpi" data-on="0"><span>Org seat</span><b>Sealed</b><em>shares</em></div>
        <div className="pc-control-desk__kpi" data-on="0"><span>Armed</span><b>no</b><em>status</em></div>
        <div className="pc-control-desk__kpi" data-on="1"><span>Role</span><b>${seat.role || role || "—"}</b><em>principal</em></div>
      </div>
      <${Card} title="Control posture" meta="status">
        <p className="note">${softControlErrorOf(post.detail || posture.error)
          || "Platform staff, or an organisation admin for shares, is required."}</p>
      <//>
      <${Alert} tone="info" title="Platform control gated"
        say="Platform staff, or an account admin (organisation shares), is required."/>
    </div>`;
  }
  if (seat.platformAllowed !== true && seat.orgAllowed === true) {
    const showRepro = tabAllowed("reproduction");
    const orgTab = showRepro && String(sub || "") === "reproduction" ? "reproduction" : "shares";
    return html`<div className="pc-control-desk pc-control-desk--liquid"
      data-cmc-page="page132-control" data-cmc-legacy="page84-control" data-cmc-control="page37"
      data-cmc-residual="page218-control" data-cmc-org-repro=${showRepro ? "1" : "0"}>
      <div className="pc-control-desk__rail">
        <div>
          <p className="eyebrow">Control · organisation plane open</p>
          <h1 className="pc-control-desk__title">Organisation seat only</h1>
        </div>
        <div className="btn-row pc-control-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${seat.reReadAllowed !== true}
            onClick=${reRead}><${Ico} d="refresh" w=${13}/> Re-read</button>
        </div>
      </div>
      <div className="pc-control-desk__kpis" aria-label="Control dual buses">
        <div className="pc-control-desk__kpi" data-on="0"><span>Platform seat</span><b>Sealed</b><em>SA tabs</em></div>
        <div className="pc-control-desk__kpi" data-on="1"><span>Org seat</span><b>Live</b><em>shares</em></div>
        <div className="pc-control-desk__kpi" data-on=${seat.armed ? "1" : "0"}><span>Armed</span><b>${seat.armed ? "yes" : "no"}</b><em>status</em></div>
        <div className="pc-control-desk__kpi" data-on="1"><span>Role</span><b>${seat.role || role || "—"}</b><em>principal</em></div>
      </div>
      <${Alert} tone="info" title="Platform seat sealed"
        say="Account admins may use Organisation shares and the R0–R4 Reproduction ladder (read). Domains, custody, group shares, multi-site HQ, security and backup stay with platform staff."/>
      <nav className="pc-control-desk__tabs" aria-label="Organisation Control sections" style=${{ marginTop: 10 }}>
        <button type="button" className="pc-control-desk__tab" data-on=${orgTab === "shares" ? "1" : "0"}
          onClick=${() => go("/control/shares")}>Org shares</button>
        ${showRepro ? html`<button type="button" className="pc-control-desk__tab"
          data-on=${orgTab === "reproduction" ? "1" : "0"}
          onClick=${() => go("/control/reproduction")}>Reproduction</button>` : null}
      </nav>
      <div className="pc-control-desk__body" style=${{ marginTop: 14 }}>
        ${orgTab === "reproduction" && showRepro
          ? html`<${ReproductionDesk} toast=${toast}/>`
          : html`<${SharesPane} toast=${toast} product=${post.cross_tenant_share_flag} allowed=${sharesPaneOpen}/>`}
      </div>
    </div>`;
  }

  return html`<div className="pc-control-desk pc-control-desk--liquid"
    data-cmc-page="page132-control" data-cmc-legacy="page84-control" data-cmc-control="page37"
    data-cmc-residual="page218-control">
    <div className="pc-control-desk__rail">
      <div>
        <p className="eyebrow">Staff Control desk · dual seat</p>
        <h1 className="pc-control-desk__title">Platform control dual</h1>
        <p className="note">
          Domains · custody · group shares · multi-site HQ · organisation shares · security · backup.
          Each tab opens only when your Live seat allows it — Page37 buses retained.
        </p>
        <${Alert} tone="info" title="Control dual seats"
          say=${`Platform ${seat.platformAllowed ? "open" : "sealed"} · organisation ${seat.orgAllowed ? "open" : "sealed"} · ${seat.role || role || "—"}.`}/>
        ${seat.platformAllowed !== true ? html`<${Alert} tone="warn" title="Platform seat sealed"
          say="Platform tabs need a root SA or platform staff seat."/>` : null}
        ${seat.orgAllowed !== true ? html`<${Alert} tone="info" title="Organisation seat sealed"
          say="Organisation shares stay closed for this account."/>` : null}
      </div>
      <div className="btn-row pc-control-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${posture.loading || seat.reReadAllowed !== true}
          onClick=${reRead}><${Ico} d="refresh" w=${13}/> Re-read</button>
        ${seat.platformAllowed === true ? html`
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/custody")}>Custody desk</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/vault")}>Backup export</button>
        ` : null}
      </div>
    </div>
    <div className="pc-control-desk__kpis" aria-label="Control dual buses">
      <div className="pc-control-desk__kpi" data-on=${seat.platformAllowed ? "1" : "0"}>
        <span>Platform seat</span><b>${seat.platformAllowed ? "Live" : "Sealed"}</b><em>SA tabs</em>
      </div>
      <div className="pc-control-desk__kpi" data-on=${seat.orgAllowed ? "1" : "0"}>
        <span>Org seat</span><b>${seat.orgAllowed ? "Live" : "Sealed"}</b><em>shares</em>
      </div>
      <div className="pc-control-desk__kpi" data-on=${seat.armed ? "1" : "0"}>
        <span>Armed</span><b>${seat.armed ? "yes" : "no"}</b><em>status</em>
      </div>
      <div className="pc-control-desk__kpi" data-on="1">
        <span>Role</span><b>${seat.role || role || "—"}</b><em>principal</em>
      </div>
    </div>
    <${Card} title="Control posture" meta="status">
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="domains" v=${post.domains_allowed === true ? "open" : "closed"}/>
        <${KV} k="custody" v=${post.custody_allowed === true ? "open" : "closed"}/>
        <${KV} k="group shares" v=${post.ng_shares_allowed === true ? "open" : "closed"}/>
        <${KV} k="federation" v=${post.federation_allowed === true ? "open" : (post.federation_flag === false ? "flag off" : "closed")}/>
        <${KV} k="org shares" v=${post.shares_allowed === true ? "open" : (post.cross_tenant_share_flag === false ? "flag off" : "closed")}/>
        <${KV} k="security" v=${post.security_allowed === true ? "open" : "closed"}/>
        <${KV} k="backup" v=${post.backup_allowed === true ? "open" : "closed"}/>
      </div>
      ${post.detail ? html`<p className="note" style=${{ marginTop: 8 }}>${softControlErrorOf(post.detail)}</p>` : null}
      ${posture.error ? html`<p className="note" style=${{ marginTop: 8 }}>${softControlErrorOf(posture.error)}</p>` : null}
    <//>
    <nav className="pc-control-desk__tabs" aria-label="Control sections">
      ${tabsPaint.map((t) => html`
        <button key=${t.id} type="button" className="pc-control-desk__tab"
          data-on=${tab === t.id ? "1" : "0"}
          disabled=${!tabAllowed(t.id)}
          onClick=${() => {
            if (!tabAllowed(t.id)) {
              toast && toast("That Control tab is closed on your account", "warn");
              return;
            }
            go("/control/" + t.id);
          }}>${t.label}</button>`)}
    </nav>
    <div className="pc-control-desk__body">
      ${tab === "domains" && seat.platformAllowed === true && html`<${DomainsPane} toast=${toast} allowed=${domainsPaneOpen}/>`}
      ${tab === "custody" && seat.platformAllowed === true && html`<${Custody} toast=${toast}/>`}
      ${tab === "ng-shares" && seat.platformAllowed === true && html`<${NgSharesPane} toast=${toast} allowed=${ngSharesPaneOpen}/>`}
      ${tab === "federation" && seat.platformAllowed === true && html`<${FederationPane} toast=${toast} me=${me} allowed=${federationPaneOpen}/>`}
      ${tab === "shares" && seat.orgAllowed === true && html`<${SharesPane} toast=${toast} product=${post.cross_tenant_share_flag} allowed=${sharesPaneOpen}/>`}
      ${tab === "security" && seat.platformAllowed === true && html`<${SecurityDesk} toast=${toast}/>`}
      ${tab === "backup" && seat.platformAllowed === true && html`<${BackupPane} toast=${toast} me=${me} allowed=${backupPaneOpen}/>`}
      ${tab === "reproduction" && (seat.platformAllowed === true || role === "admin" || isSa) && html`<${ReproductionDesk} toast=${toast}/>`}
    </div>
  </div>`;
}

function DomainsPane({ toast, allowed = true }) {
  const domains = useApi(allowed ? path("get__sa_platform_domains") : "");
  const pack = domains.data || {};
  const plane = controlPlaneOf(pack);
  const residual = residualDomainsOf(pack);
  const kpis = useMemo(() => domainsKpisOf(pack), [domains.data]);
  const ds = (pack.device_state_pg_flags && typeof pack.device_state_pg_flags === "object")
    ? pack.device_state_pg_flags : {};
  const honesty = (pack.honesty && typeof pack.honesty === "object") ? pack.honesty : {};

  if (!allowed) {
    return html`<${Alert} tone="info" title="Domains seat closed"
      say="Domains need a platform staff seat with device or security capability. Matrix rows are never invented."/>`;
  }

  return html`<${F} className="pc-domains-pane">
    <div className="btn-row" style=${{ marginBottom: 12 }}>
      <button className="btn btn--ghost btn--sm" type="button" disabled=${!!domains.loading}
        onClick=${() => { domains.reload && domains.reload(); toast && toast("Domains matrix re-read", "go"); }}>
        <${Ico} d="refresh" w=${13}/> Re-read</button>
    </div>

    <div className="pc-control-desk__kpis" role="group" aria-label="Domains KPIs">
      ${[
        ["Control-plane", kpis.plane, kpis.plane ? "1" : "0", "Domain matrix"],
        ["Residual", kpis.residual, kpis.residual ? "1" : "0", "legacy storage"],
        [
          "Primary DB",
          kpis.primaryDb === null ? "—" : (kpis.primaryDb ? "on" : "off"),
          kpis.primaryDb ? "1" : "0",
          "Live flag",
        ],
        [
          "Full primary-database claim",
          kpis.fullPg === null ? "—" : (kpis.fullPg ? "yes" : "no"),
          kpis.fullPg ? "1" : "0",
          "honesty",
        ],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="pc-control-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b>${typeof v === "number" ? html`<${Ticker} value=${v}/>` : v}</b>
          <em>${em}</em>
        </div>`)}
    </div>

    ${domains.loading && !domains.data
      ? html`<p className="note">Reading domains matrix…</p>`
      : domains.error
        ? html`<${Alert} tone="warn" title="Domains unavailable"
            say=${softControlErrorOf(domains.error)}/>`
        : html`<${F}>
            <${Card} title="Platform readiness" meta="Live overview">
              <div className="grid g2">
                <${KV} k="Platform primary DB" v=${flagCell(pack.platform_pg_enabled)}/>
                <${KV} k="DB connection ready" v=${flagCell(pack.platform_pg_dsn_configured)}/>
                <${KV} k="Device state store" v=${flagCell(pack.device_state_pg_enabled)}/>
                <${KV} k="Console data path"
                  v=${String(honesty.console_contract || "—")
                    .replace(/paginated_devices/gi, "Paged device list")
                    .replace(/redis_read_cache/gi, "read cache")
                    .replace(/\s*\+\s*/g, " · ")}/>
              </div>
            <//>

            <${Card} title="Device state flags" meta="Live flags" cls="mt">
              <div className="grid g2">
                <${KV} k="Active" v=${flagCell(ds.enabled)}/>
                <${KV} k="Mirror writes" v=${flagCell(ds.mirror_write)}/>
                <${KV} k="Write SoR primary" v=${flagCell(ds.write_sor)}/>
                <${KV} k="Legacy write stopped" v=${flagCell(ds.sqlite_write_disabled)}/>
              </div>
            <//>

            <${Card} title="Honesty claims" meta="Live booleans only" cls="mt">
              <div className="grid g2">
                <${KV} k="10k HTTP SLO claim" v=${flagCell(honesty.claim_10k_http_slo)}/>
                <${KV} k="Full primary readiness" v=${flagCell(honesty.claim_full_pg)}/>
              </div>
              ${(honesty.claim_full_pg === false || honesty.claim_full_pg == null)
                ? html`<${Alert} tone="info" title="Not fully cut over"
                    say="Full primary readiness is still off — residual areas remain listed below. Nothing turns green without Live flags."/>`
                : null}
            <//>

            <${Card} title="Active data domains" meta=${plane.length + " control-plane rows"} cls="mt">
              ${!plane.length
                ? html`<p className="note">Empty — no control-plane domain rows yet.</p>`
                : html`<table className="tbl">
                    <thead><tr>
                      <th>Domain</th><th>Primary store</th><th>Read</th>
                      <th>Write primary</th><th>Legacy stopped</th><th>Mirror</th>
                    </tr></thead>
                    <tbody>
                      ${plane.map((row) => {
                        const id = row && row.id;
                        const flags = (row && row.pg_flags) || {};
                        return html`<tr key=${id}>
                          <td>${domainLabel(id)}</td>
                          <td>${storeLabel(row && row.effective_sor)}</td>
                          <td className="mono">${flagCell(flags.read)}</td>
                          <td className="mono">${flagCell(flags.write_sor)}</td>
                          <td className="mono">${flagCell(flags.sqlite_write_disabled)}</td>
                          <td className="mono">${flagCell(flags.mirror_write)}</td>
                        </tr>`;
                      })}
                    </tbody>
                  </table>`}
            <//>

            <${Card} title="Still on older storage" meta=${residual.length + " residual"} cls="mt">
              ${!residual.length
                ? html`<p className="note">No residual domains listed — Empty residual set.</p>`
                : html`<table className="tbl">
                    <thead><tr><th>Area</th><th>Notes</th></tr></thead>
                    <tbody>
                      ${residual.map((row, i) => html`<tr key=${(row && row.id) || i}>
                        <td>${domainLabel((row && (row.label || row.id)) || "")}</td>
                        <td className="note" style=${{ margin:0 }}>${residualNote(row && row.notes)}</td>
                      </tr>`)}
                    </tbody>
                  </table>`}
            <//>
          <//>`}
  <//>`;
}

function FederationPane({ toast, me, allowed }) {
  // Live posture only — catalog/me invent of federation pane deleted.
  const fedOn = allowed === true;
  const metrics = useApi(fedOn ? path("get__federation_metrics") : null);
  const tenants = useApi(fedOn ? path("get__federation_tenants") : null);
  const { run, busy, envelope } = useAction();
  const [action, setAction] = useState("join");
  const [fedId, setFedId] = useState("");
  const tenantRows = fedTenantRowsOf(tenants.data);
  const item = federationMetricsItemOf(metrics.data);
  const tenantCountLive = item.tenant_count != null
    ? item.tenant_count
    : (tenants.data && tenants.data.count != null ? tenants.data.count : tenantRows.length);

  const submit = async () => {
    const out = await run(path("post__federation_consent"), {
      body: { action, federation_id: fedId.trim() || undefined },
    });
    if (!out) {
      toast && toast("Federation consent refused · nothing was changed", "bad");
      return;
    }
    toast && toast("Consent submitted", "go");
    tenants.reload && tenants.reload();
    metrics.reload && metrics.reload();
  };

  if (!fedOn) {
    return html`<${Alert} tone="info" title="Multi-site HQ unavailable"
      say="Multi-site HQ is off for this deployment. Partner metrics stay empty until Live opens the seat."/>`;
  }

  return html`<${F}>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
    <div className="btn-row" style=${{ marginBottom: 12 }}>
      <button className="btn btn--ghost btn--sm" type="button" disabled=${!!metrics.loading}
        onClick=${() => {
          metrics.reload && metrics.reload();
          tenants.reload && tenants.reload();
          toast && toast("Federation re-read", "go");
        }}>
        <${Ico} d="refresh" w=${13}/> Re-read</button>
    </div>
    <${Card} title="Network health" meta="Live network health">
      ${metrics.error
        ? html`<${Alert} tone="warn" title="Metrics unavailable" say=${metrics.error}/>`
        : metrics.loading && !metrics.data
          ? html`<p className="note">Reading metrics…</p>`
          : html`<div className="grid g2">
              <${KV} k="Network ID" v=${numOrDash(item.federation_id)}/>
              <${KV} k="Organizations" v=${numOrDash(tenantCountLive)}/>
              <${KV} k="Devices" v=${numOrDash(item.device_total)}/>
              <${KV} k="Alarms" v=${numOrDash(item.alarm_count)}/>
              <${KV} k="Avg alarm rate" v=${numOrDash(item.avg_alarm_rate)}/>
              <${KV} k="Avg response (ms)" v=${numOrDash(item.avg_response_ms)}/>
              <${KV} k="Avg fault rate" v=${numOrDash(item.avg_fault_rate)}/>
              ${item.status != null && item.status !== ""
                ? html`<${KV} k="Status (server)" v=${String(item.status)}/>`
                : html`<${KV} k="Status (server)" v="—"/>`}
            </div>
            ${!(item.status != null && item.status !== "")
              ? html`<p className="note" style=${{ marginTop:8 }}>No status field from the server yet.</p>`
              : null}`}
    <//>
    <${Card} title="Organizations" meta=${tenantRows.length + " organisation row(s)"} cls="mt">
      ${tenants.error
        ? html`<${Alert} tone="warn" title="Tenants unavailable" say=${tenants.error}/>`
        : !tenantRows.length
          ? html`<p className="note">Empty — no partner organization aggregates yet.</p>`
          : html`<table className="tbl">
              <thead><tr>
                <th>Organization</th><th>Devices</th><th>Alarms</th>
                <th>Alarm rate</th><th>Avg response</th><th>Fault rate</th><th>Updated</th>
              </tr></thead>
              <tbody>
                ${tenantRows.slice(0, 40).map((t, i) => {
                  const row = t || {};
                  const tid = row.tenant_id || row.id || i;
                  return html`<tr key=${tid}>
                    <td className="mono">${numOrDash(row.tenant_id || row.id || row.name)}</td>
                    <td className="mono">${numOrDash(row.device_total)}</td>
                    <td className="mono">${numOrDash(row.alarm_count)}</td>
                    <td className="mono">${numOrDash(row.alarm_rate)}</td>
                    <td className="mono">${numOrDash(row.avg_response_ms)}</td>
                    <td className="mono">${numOrDash(row.fault_rate)}</td>
                    <td>${formatUpdatedMs(row.updated_at_ms)}</td>
                  </tr>`;
                })}
              </tbody>
            </table>`}
    <//>
    <${Card} title="Submit consent" meta="Live consent" cls="mt">
      <div className="grid g2">
        <${Field} label="Action">
          <select className="inp" value=${action} onChange=${e=>setAction(e.target.value)}>
            <option value="join">Join</option>
            <option value="leave">Leave</option>
            <option value="renew">Renew</option>
          </select>
        <//>
        <${Field} label="Federation id (optional)">
          <${Input} icon="chip" value=${fedId} onChange=${e=>setFedId(e.target.value)}/>
        <//>
      </div>
      <button className="btn btn--go btn--sm" disabled=${busy} onClick=${submit} style=${{ marginTop:10 }}>
        Submit consent</button>
    <//>
  <//>`;
}

function SharesPane({ toast, product, allowed = true }) {
  const shares = usePolled(allowed ? path("get__shares") : null, 30000);
  const { run, busy, envelope } = useAction();
  const rows = shareRowsOf(shares.data);
  const [scopeType, setScopeType] = useState("device");
  const [scopeTarget, setScopeTarget] = useState("");
  const [ttl, setTtl] = useState("24");
  const [canOperate, setCanOperate] = useState(false);
  const [redeemKey, setRedeemKey] = useState("");
  const [lastKey, setLastKey] = useState("");

  const createKey = async () => {
    if (!scopeTarget.trim()) { toast && toast("Scope target required", "warn"); return; }
    const out = await run(path("post__shares_keys"), {
      body: {
        scope_type: scopeType,
        scope_target: scopeTarget.trim(),
        ttl_hours: Number(ttl) || 24,
        can_view: true,
        can_operate: !!canOperate,
      },
    });
    if (!out) { toast && toast("Share key refused · nothing was changed", "bad"); return; }
    const k = String(out.key || out.id || "");
    setLastKey(k);
    toast && toast(k ? "Share key created · copy once" : "Share created", "go");
    shares.reload && shares.reload();
  };

  const redeem = async () => {
    if (!redeemKey.trim()) { toast && toast("Key required", "warn"); return; }
    const out = await run(path("post__shares_redeem"), { body: { key: redeemKey.trim() } });
    if (!out) { toast && toast("Redeem refused · nothing was changed", "bad"); return; }
    toast && toast("Share redeemed", "go");
    setRedeemKey("");
    shares.reload && shares.reload();
  };

  const revoke = async (id) => {
    if (!id) return;
    const out = await run(path("delete__shares_p", { pathParams: [id] }), { method: "DELETE" });
    if (!out) { toast && toast("Revoke refused · nothing was changed", "bad"); return; }
    toast && toast("Share revoked", "warn");
    shares.reload && shares.reload();
  };

  if (!allowed) {
    return html`<${Alert} tone="info" title="Org shares seat closed"
      say="Organisation shares stay closed without a Live share seat."/>`;
  }
  if (product === false) {
    return html`<${Alert} tone="warn" title="Cross-tenant share product unarmed"
      say="Cross-organisation share is off for this deployment. Share keys stay closed until it is armed."/>`;
  }

  return html`<${F}>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
    <${Card} title="Organization shares" meta="status">
      <div className="btn-row" style=${{ marginBottom: 8 }}>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => { shares.reload && shares.reload(); toast && toast("Shares re-read", "go"); }}>
          <${Ico} d="refresh" w=${13}/> Re-read</button>
      </div>
      ${shares.error
        ? html`<${Alert} tone="warn" title="Shares unavailable"
            say=${softControlErrorOf(shares.error)}/>`
        : !rows.length
          ? html`<p className="note">No active shares yet.</p>`
          : rows.slice(0, 32).map((s, i) => {
              const id = String(s.id || s.share_id || "");
              return html`<div key=${id || i} className="glass" style=${{ padding: "10px 12px", marginBottom: 8, borderRadius: "var(--r-md)" }}>
                <${KV} k="Id" v=${id || "—"}/>
                <${KV} k="Scope" v=${String(s.scope_type || "") + " · " + String(s.scope_target || s.target || "—")}/>
                <button type="button" className="btn btn--danger btn--xs" disabled=${busy || !id}
                  onClick=${() => revoke(id)}>Revoke</button>
              </div>`;
            })}
    <//>
    <${Card} title="Mint share key" meta="Mint" cls="mt">
      <${Field} label="Scope type">
        <select className="inp" value=${scopeType} onChange=${e=>setScopeType(e.target.value)}>
          <option value="device">Device</option>
          <option value="notification_group">Notification group</option>
        </select>
      <//>
      <${Field} label="Target (serial or group)">
        <${Input} icon="chip" value=${scopeTarget} onChange=${e=>setScopeTarget(e.target.value)}/>
      <//>
      <${Field} label="TTL hours">
        <${Input} icon="clock" value=${ttl} onChange=${e=>setTtl(e.target.value)}/>
      <//>
      <label className="note" style=${{ display:"flex", gap:8, alignItems:"center", marginTop:8 }}>
        <input type="checkbox" checked=${canOperate} onChange=${e=>setCanOperate(!!e.target.checked)}/>
        Allow operate grant
      </label>
      <button className="btn btn--go btn--sm" disabled=${busy} onClick=${createKey} style=${{ marginTop:8 }}>
        Create key</button>
      ${lastKey && html`<${Alert} tone="warn" title="Copy once"
        say=${"Key shown once: " + lastKey}/>`}
    <//>
    <${Card} title="Redeem key" meta="Redeem" cls="mt">
      <${Field} label="Share key">
        <${Input} icon="key" value=${redeemKey} onChange=${e=>setRedeemKey(e.target.value)}/>
      <//>
      <button type="button" className="btn btn--sm" disabled=${busy} onClick=${redeem} style=${{ marginTop: 8 }}>
        Redeem into this account</button>
    <//>
  <//>`;
}

/** Soft≠Live: SA notification-group shares ledger + orphan cleanup. */
function NgSharesPane({ toast, allowed = true }) {
  const [owner, setOwner] = useState("");
  const [grantee, setGrantee] = useState("");
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [q, setQ] = useState({ owner: "", grantee: "", revoked: false });
  const query = useMemo(() => {
    const out = { limit: 100 };
    if (q.owner.trim()) out.owner_admin = q.owner.trim();
    if (q.grantee.trim()) out.grantee_username = q.grantee.trim();
    if (q.revoked) out.include_revoked = true;
    return out;
  }, [q]);
  const list = useApi(allowed ? path("get__sa_notification-groups_shares", { query }) : "");
  const { run, busy, envelope } = useAction();
  const items = (list.data && (list.data.items || list.data.shares)) || [];

  const applyFilter = () => setQ({ owner, grantee, revoked: includeRevoked });

  const cleanup = async () => {
    if (!allowed) {
      toast && toast("Group shares cleanup is closed on your account", "warn");
      return;
    }
    const out = await run(path("post__admin_notification-groups_shares_cleanup-orphans"), {
      method: "POST",
      body: {},
    });
    if (!out) { toast && toast("Cleanup refused · nothing was changed", "bad"); return; }
    const n = out.removed != null ? out.removed : (out.count != null ? out.count : "ok");
    toast && toast("Orphan share cleanup · " + n, "go");
    list.reload && list.reload();
  };

  if (!allowed) {
    return html`<${Alert} tone="info" title="Group shares seat closed"
      say="Group shares need a platform staff seat with device capability."/>`;
  }

  return html`<${F}>
    <${Alert} tone="info" title="Singular Live group-shares desk · Page168"
      say="Group shares ledger with orphan cleanup. Empty rows mean nothing Live matched the filter."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
    <${Card} title="Filters" meta="re-query">
      <div className="grid g2">
        <${Field} label="Owner admin">
          <${Input} icon="user" value=${owner} onChange=${(e) => setOwner(e.target.value)}/>
        <//>
        <${Field} label="Grantee">
          <${Input} icon="user" value=${grantee} onChange=${(e) => setGrantee(e.target.value)}/>
        <//>
      </div>
      <label className="note" style=${{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
        <input type="checkbox" checked=${includeRevoked} onChange=${(e) => setIncludeRevoked(!!e.target.checked)}/>
        Include revoked
      </label>
      <div className="btn-row" style=${{ marginTop: 10 }}>
        <button type="button" className="btn btn--go btn--sm" onClick=${applyFilter}>Apply filter</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => { list.reload && list.reload(); toast && toast("Group shares re-read", "go"); }}>
          <${Ico} d="refresh" w=${13}/> Re-read</button>
        <button type="button" className="btn btn--danger btn--sm" disabled=${busy} onClick=${cleanup}>
          Cleanup orphans</button>
      </div>
    <//>
    <${Card} title="Group shares" meta=${list.loading ? "reading…" : (items.length + " rows")} cls="mt">
      ${list.error
        ? html`<${Alert} tone="warn" title="Group shares unavailable" say=${list.error}/>`
        : !items.length
          ? html`<p className="note">Empty — no group shares for this filter.</p>`
          : items.slice(0, 40).map((s, i) => html`
              <div key=${s.id || s.share_id || i} className="row">
                <span className="st"/>
                <span className="main">
                  <b>${String(s.group_display || s.group_key || s.notification_group || "group")}</b>
                  <span>${String(s.owner_admin || "—")} → ${String(s.grantee_username || s.grantee || "—")}
                    · ${String(s.status || (s.revoked_at ? "revoked" : "active"))}</span>
                </span>
              </div>`)}
    <//>
  <//>`;
}

function BackupPane({ toast, me, allowed }) {
  // Live backup_allowed only — catalog/role invent deleted.
  const backupOn = allowed === true;
  const schedule = useApi(backupOn ? safeControlPath("get__admin_backup_schedule") : null);
  const pgStatus = useApi(backupOn ? safeControlPath("get__admin_pg-backup_status") : null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const drillRef = useRef(null);
  const ok = /^[0-9a-fA-F]{64}$/.test(key);

  const drill = async () => {
    if (!ok) { toast("64-hex backup key required", "warn"); return; }
    const file = drillRef.current && drillRef.current.files && drillRef.current.files[0];
    if (!file) { toast("Pick an encrypted backup file — empty drill forbidden", "warn"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "sentinel-backup.enc");
      const pack = await api(path("post__admin_backup_restore-drill"), {
        method: "POST",
        body: fd,
        headers: { "X-Backup-Encryption-Key": key },
        timeoutMs: 120000,
      });
      const untouched = pack && pack.production_touched === false;
      toast(untouched
        ? "Restore drill complete · production untouched"
        : "Restore drill finished · check validation", "go");
      schedule.reload && schedule.reload();
      pgStatus.reload && pgStatus.reload();
    } catch (e) {
      toast(String((e && e.message) || e || "Drill refused"), "bad");
    } finally {
      setBusy(false);
    }
  };

  if (!backupOn) {
    return html`<${Alert} tone="info" title="Backup · Verify"
      say="Backup worker / manual backup catalog flags are off.."/>`;
  }

  const s = schedule.data || {};
  const honesty = (s && s.honesty) || {};
  const sqliteH = honesty.sqlite_file_export || {};
  const pgH = honesty.pg_backup_ops || {};
  const pgLive = (pgStatus.data && pgStatus.data.pg) || {};
  const pgTier = String(pgH.tier || (pgLive.tools_on_host ? "Live" : (pgLive.configured ? "ops_image" : "off")));
  const toolsOn = pgH.tools_on_host === true || pgLive.tools_on_host === true;
  const pgConfigured = (pgH.armed === true && (pgTier === "Live" || pgTier === "ops_image"))
    || (pgH.armed == null && pgLive.configured === true);
  // Never invent “dump live” when tools are off the host — honest tiers only.
  const pgLabel = !pgConfigured
    ? `${pgTier} · not set up`
    : toolsOn
      ? `${pgTier} · live database dump`
      : `${pgTier} · ops backup host (dump tools not on this API)`;
  const sqliteTier = String(sqliteH.tier || "—");
  return html`<${F} data-cmc-residual="page231-control-backup">
    <${Card} title="Backup schedule" meta="Live schedule">
      ${schedule.error
        ? html`<${Alert} tone="warn" title="Schedule unavailable" say=${softControlErrorOf(schedule.error)}/>`
        : html`<${KV} k="Enabled" v=${s.enabled != null ? (s.enabled ? "yes" : "no") : String(s.status || "—")}/>
               <${KV} k="Cadence" v=${String(s.cron || s.cadence || s.interval || s.interval_seconds || "—")}/>
               <${KV} k="Last run" v=${String(s.last_run_at || s.last_ok_at || "—")}/>
               <${KV} k="Legacy file export" v=${sqliteTier + (sqliteH.soft_under_eol ? " · refused under end-of-life" : "")}/>
               <${KV} k="Database backup ops" v=${pgLabel}/>`}
      ${sqliteH.soft_under_eol
        ? html`<${Alert} tone="warn" title="Legacy file export refused"
            say=${String(sqliteH.note || "Legacy file export is refused under end-of-life policy. Use live database backup ops.")}/>`
        : null}
      ${pgConfigured && !toolsOn
        ? html`<${Alert} tone="warn" title="Backup tools on ops host only"
            say=${String((pgLive.note || pgH.note) || "Use the ops or backup host. Dump success is not claimed on this API host.")}/>`
        : null}
      ${!pgConfigured
        ? html`<${Alert} tone="info" title="Database disaster recovery not armed"
            say=${String(pgH.note || "Primary database backup must be armed by operations before dumps run.")}/>`
        : null}
      <p className="note">Encrypted export opens under Backup export. Schedule and restore drill stay here. A green “backed up” state only appears after Live schedule/status succeed.</p>
      <div className="btn-row" style=${{ gap: 8, flexWrap: "wrap" }}>
        <button className="btn btn--ghost btn--sm" onClick=${()=>{
          schedule.reload && schedule.reload();
          pgStatus.reload && pgStatus.reload();
          toast && toast("Backup schedule re-read", "go");
        }}>Re-read</button>
        <button className="btn btn--ghost btn--sm" onClick=${()=>go("/sa/vault")}>Open backup export</button>
      </div>
    <//>
    <${Card} title="Restore drill" meta="Restore drill" cls="mt"
        sub="Isolated decrypt and validation only. Production is never written. An empty file never counts as a successful drill.">
      <${Field} label="Backup encryption key (64 hex)">
        <${Input} icon="key" value=${key} onChange=${e=>setKey(e.target.value.trim())}
          placeholder="64 hex characters"/>
      <//>
      <${Field} label="Encrypted backup file">
        <input ref=${drillRef} type="file" className="inp" accept=".enc,.bin,*/*"/>
      <//>
      <button className="btn btn--sm" disabled=${busy || !ok} onClick=${drill} style=${{ marginTop:8 }}>
        Run restore drill</button>
    <//>
  <//>`;
}
